import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Instagram, Facebook, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-deep-forest text-soft-ivory" data-testid="footer">
      <div className="max-w-7xl mx-auto section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="font-playfair text-2xl font-semibold">
              Tiger Wellness Club
            </h3>
            <p className="text-desert-sand text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h4 className="font-inter text-lg font-semibold">
              {t('footer.newsletter')}
            </h4>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <Input
                type="email"
                placeholder={t('footer.newsletterPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-soft-ivory/10 border-soft-ivory/20 text-soft-ivory placeholder:text-soft-ivory/50"
                required
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-tiger-orange hover:bg-tiger-orange/90 text-soft-ivory"
                data-testid="newsletter-submit-button"
              >
                {isSubmitting ? 'Subscribing...' : t('footer.subscribe')}
              </Button>
            </form>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h4 className="font-inter text-lg font-semibold">{t('footer.links')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/about" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="/shop" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  Shop
                </a>
              </li>
              <li>
                <a href="/journal" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  Journal
                </a>
              </li>
              <li>
                <a href="/account" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  My Account
                </a>
              </li>
            </ul>
          </div>

          {/* Legal & Social */}
          <div className="space-y-4">
            <h4 className="font-inter text-lg font-semibold">{t('footer.legal')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/terms" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/privacy" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/contact" className="text-desert-sand hover:text-soft-ivory transition-colors">
                  Contact
                </a>
              </li>
            </ul>
            <div className="flex space-x-4 pt-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-desert-sand hover:text-soft-ivory transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-desert-sand hover:text-soft-ivory transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="mailto:hello@tigerwellness.com"
                className="text-desert-sand hover:text-soft-ivory transition-colors"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-soft-ivory/10 flex flex-col md:flex-row items-center justify-between text-sm text-desert-sand">
          <p>© 2025 Tiger Wellness Club. All rights reserved.</p>
          <p className="flex items-center space-x-1 mt-4 md:mt-0">
            <span>Made with</span>
            <Heart className="w-4 h-4 text-tiger-orange fill-tiger-orange" />
            <span>for your wellness</span>
          </p>
        </div>
      </div>
    </footer>
  );
}